import { usePage } from '@inertiajs/react';
import { useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    Chip,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Tab,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Snackbar,
    Alert,
    Tooltip
} from '@mui/material';
import {
    Visibility as VisibilityIcon,
    Home as HomeIcon,
    Phone as PhoneIcon,
    Delete as DeleteIcon,
    Block as BlockIcon,
    CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { router } from '@inertiajs/react';
import { formatPhoneNumber } from '../../utils/formatPhoneNumber';
import axiosClient from '../../services/axiosClient';

interface Customer {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
}

interface Listing {
    id: string;
    title: string;
    full_address: string;
    rent: number;
    num_bedrooms: number;
    num_bathrooms: number;
    status: string;
    is_active: boolean;
    created_at: string;
    customer: Customer;
}

interface Props {
    all_listings: Listing[];
    active_listings: Listing[];
    deactivated_listings: Listing[];
    all_count: number;
    active_count: number;
    deactivated_count: number;
    [key: string]: any;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`listings-tabpanel-${index}`}
            aria-labelledby={`listings-tab-${index}`}
            {...other}
        >
            {value === index && <Box>{children}</Box>}
        </div>
    );
}

const getStatusColor = (status: string, isActive: boolean): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    if (!isActive) return 'error';
    switch (status) {
        case 'published':
            return 'success';
        case 'ready_to_publish':
            return 'info';
        case 'initial':
        case 's3_upload_pending':
            return 'warning';
        case 'tenant_secured':
            return 'secondary';
        case 's3_upload_failed':
            return 'error';
        default:
            return 'default';
    }
};

const getDisplayStatus = (status: string, isActive: boolean): string => {
    if (!isActive) return 'Deactivated';
    switch (status) {
        case 'published':
            return 'Published';
        case 'ready_to_publish':
            return 'Ready to Publish';
        case 'initial':
            return 'Initial';
        case 's3_upload_pending':
            return 'Uploading';
        case 'tenant_secured':
            return 'Tenant Secured';
        case 's3_upload_failed':
            return 'Upload Failed';
        default:
            return status;
    }
};

const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
};

const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(amount);
};

export default function AdminListingsPage() {
    const { props } = usePage<Props>();
    const { all_listings, active_listings, deactivated_listings, all_count, active_count, deactivated_count } = props;

    const [tabValue, setTabValue] = useState(1); // Default to Active tab
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
    const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);
    const [selectedListing, setSelectedListing] = useState<Listing | null>(null);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success'
    });
    const [isLoading, setIsLoading] = useState(false);

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleDeleteClick = (listing: Listing, e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedListing(listing);
        setDeleteDialogOpen(true);
    };

    const handleDeactivateClick = (listing: Listing, e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedListing(listing);
        setDeactivateDialogOpen(true);
    };

    const handleReactivateClick = (listing: Listing, e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedListing(listing);
        setReactivateDialogOpen(true);
    };

    const handleDelete = async () => {
        if (!selectedListing) return;
        setIsLoading(true);
        try {
            await axiosClient.delete(`/admin/listings/${selectedListing.id}`);
            setSnackbar({ open: true, message: 'Listing deleted successfully', severity: 'success' });
            setDeleteDialogOpen(false);
            router.reload();
        } catch (error: any) {
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Failed to delete listing',
                severity: 'error'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeactivate = async () => {
        if (!selectedListing) return;
        setIsLoading(true);
        try {
            await axiosClient.patch(`/admin/listings/${selectedListing.id}/deactivate`);
            setSnackbar({ open: true, message: 'Listing deactivated successfully', severity: 'success' });
            setDeactivateDialogOpen(false);
            router.reload();
        } catch (error: any) {
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Failed to deactivate listing',
                severity: 'error'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleReactivate = async () => {
        if (!selectedListing) return;
        setIsLoading(true);
        try {
            await axiosClient.patch(`/admin/listings/${selectedListing.id}/reactivate`);
            setSnackbar({ open: true, message: 'Listing reactivated successfully', severity: 'success' });
            setReactivateDialogOpen(false);
            router.reload();
        } catch (error: any) {
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Failed to reactivate listing',
                severity: 'error'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const renderListingsTable = (listings: Listing[]) => (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Property</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Address</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Rent</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Beds/Baths</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Owner</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {listings.map((listing) => (
                        <TableRow
                            key={listing.id}
                            sx={{
                                '&:hover': { bgcolor: 'grey.100' },
                                cursor: 'pointer'
                            }}
                            onClick={() => router.visit(`/admin/listings/${listing.id}`)}
                        >
                            <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {listing.title || 'Untitled'}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200 }}>
                                    {listing.full_address}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {formatCurrency(listing.rent)}/mo
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2">
                                    {listing.num_bedrooms} bd / {listing.num_bathrooms} ba
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {listing.customer.first_name} {listing.customer.last_name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {listing.customer.email}
                                </Typography>
                                {listing.customer.phone && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <PhoneIcon sx={{ fontSize: 12 }} />
                                        {formatPhoneNumber(listing.customer.phone)}
                                    </Typography>
                                )}
                            </TableCell>
                            <TableCell>
                                <Chip
                                    label={getDisplayStatus(listing.status, listing.is_active)}
                                    size="small"
                                    color={getStatusColor(listing.status, listing.is_active)}
                                />
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                    {formatDate(listing.created_at)}
                                </Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                                    <Tooltip title="View Details">
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                router.visit(`/admin/listings/${listing.id}`);
                                            }}
                                            sx={{
                                                bgcolor: 'black',
                                                color: 'white',
                                                '&:hover': { bgcolor: 'grey.800' }
                                            }}
                                        >
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    {listing.is_active ? (
                                        <Tooltip title="Deactivate">
                                            <IconButton
                                                size="small"
                                                onClick={(e) => handleDeactivateClick(listing, e)}
                                                sx={{
                                                    bgcolor: 'warning.main',
                                                    color: 'white',
                                                    '&:hover': { bgcolor: 'warning.dark' }
                                                }}
                                            >
                                                <BlockIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    ) : (
                                        <Tooltip title="Reactivate">
                                            <IconButton
                                                size="small"
                                                onClick={(e) => handleReactivateClick(listing, e)}
                                                sx={{
                                                    bgcolor: 'success.main',
                                                    color: 'white',
                                                    '&:hover': { bgcolor: 'success.dark' }
                                                }}
                                            >
                                                <CheckCircleIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Delete">
                                        <IconButton
                                            size="small"
                                            onClick={(e) => handleDeleteClick(listing, e)}
                                            sx={{
                                                bgcolor: 'error.main',
                                                color: 'white',
                                                '&:hover': { bgcolor: 'error.dark' }
                                            }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Page Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <HomeIcon sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        Listings Management
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage all listings in the system
                    </Typography>
                </Box>
            </Box>

            {/* Tabs */}
            <Paper
                elevation={0}
                sx={{
                    bgcolor: 'grey.50',
                    borderRadius: '8px',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    overflow: 'hidden'
                }}
            >
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        bgcolor: 'white'
                    }}
                >
                    <Tab label={`All (${all_count})`} />
                    <Tab label={`Active (${active_count})`} />
                    <Tab label={`Deactivated (${deactivated_count})`} />
                </Tabs>

                {/* Tab Panels */}
                <TabPanel value={tabValue} index={0}>
                    {all_listings.length > 0 ? (
                        renderListingsTable(all_listings)
                    ) : (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography variant="body1" color="text.secondary">
                                No listings found
                            </Typography>
                        </Box>
                    )}
                </TabPanel>
                <TabPanel value={tabValue} index={1}>
                    {active_listings.length > 0 ? (
                        renderListingsTable(active_listings)
                    ) : (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography variant="body1" color="text.secondary">
                                No active listings found
                            </Typography>
                        </Box>
                    )}
                </TabPanel>
                <TabPanel value={tabValue} index={2}>
                    {deactivated_listings.length > 0 ? (
                        renderListingsTable(deactivated_listings)
                    ) : (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography variant="body1" color="text.secondary">
                                No deactivated listings found
                            </Typography>
                        </Box>
                    )}
                </TabPanel>
            </Paper>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle>Delete Listing</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to permanently delete this listing?
                        <br /><br />
                        <strong>{selectedListing?.title || 'Untitled'}</strong>
                        <br />
                        {selectedListing?.full_address}
                        <br /><br />
                        This action cannot be undone. All associated data including tour bookings, prospect interactions, and photos will be permanently removed.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDelete}
                        color="error"
                        variant="contained"
                        disabled={isLoading}
                    >
                        {isLoading ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Deactivate Confirmation Dialog */}
            <Dialog
                open={deactivateDialogOpen}
                onClose={() => setDeactivateDialogOpen(false)}
            >
                <DialogTitle>Deactivate Listing</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to deactivate this listing?
                        <br /><br />
                        <strong>{selectedListing?.title || 'Untitled'}</strong>
                        <br />
                        {selectedListing?.full_address}
                        <br /><br />
                        The listing will be hidden from public view but can be reactivated later.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeactivateDialogOpen(false)} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeactivate}
                        color="warning"
                        variant="contained"
                        disabled={isLoading}
                    >
                        {isLoading ? 'Deactivating...' : 'Deactivate'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reactivate Confirmation Dialog */}
            <Dialog
                open={reactivateDialogOpen}
                onClose={() => setReactivateDialogOpen(false)}
            >
                <DialogTitle>Reactivate Listing</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to reactivate this listing?
                        <br /><br />
                        <strong>{selectedListing?.title || 'Untitled'}</strong>
                        <br />
                        {selectedListing?.full_address}
                        <br /><br />
                        The listing will become active and visible again.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReactivateDialogOpen(false)} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleReactivate}
                        color="success"
                        variant="contained"
                        disabled={isLoading}
                    >
                        {isLoading ? 'Reactivating...' : 'Reactivate'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}
